import { CareerField } from '../types';

/**
 * Comprehensive and updated career fields, each with:
 * - id: Unique identifier
 * - title: Human-readable title of the career field
 * - traits: Personality and skill traits often associated with success in the field
 * - description: High-level overview of the career field
 * - suggestedMajors: Common majors or specializations leading to success in this career path
 * - topColleges: Notable colleges/universities with strong programs in the field
 * - careerPaths: Specific roles or job titles, with brief descriptions and requirements
 */
export const careerPaths: CareerField[] = [
  // 1) TECHNOLOGY & SOFTWARE DEVELOPMENT
  {
    id: "tech",
    title: "Software Development",
    traits: ["technical", "analytical", "logical", "detail-oriented", "innovative", "computational"],
    description:
      "A career in technology involves building software, analyzing data, and working with cutting-edge innovations—from AI to cybersecurity.",
    suggestedMajors: [
      "Computer Science",
      "Software Engineering",
      "Data Science",
      "Artificial Intelligence",
      "Cybersecurity",
      "Human-Computer Interaction",
      "Information Systems"
    ],
    topColleges: [
      {
        name: "Massachusetts Institute of Technology (MIT)",
        location: "Cambridge, MA",
        specialization: "Cutting-edge research in CS and AI"
      },
      {
        name: "Stanford University",
        location: "Stanford, CA",
        specialization: "AI, ML, entrepreneurship"
      },
      {
        name: "Carnegie Mellon University",
        location: "Pittsburgh, PA",
        specialization: "AI, robotics, software engineering"
      },
      {
        name: "University of California, Berkeley",
        location: "Berkeley, CA",
        specialization: "AI, data science, and open-source"
      }
    ],
    careerPaths: [
      {
        role: "Software Engineer",
        description: "Design, develop, and test software applications",
        requirements: [
          "BS in Computer Science or related field",
          "Proficiency in programming languages",
          "Problem-solving ability"
        ]
      },
      {
        role: "Data Scientist",
        description: "Analyze large data sets and create predictive models",
        requirements: [
          "Background in statistics",
          "Machine learning expertise",
          "Programming (Python, R, SQL)"
        ]
      },
      {
        role: "AI Engineer",
        description: "Develop AI and machine learning solutions",
        requirements: [
          "AI/ML specialization",
          "Advanced mathematics",
          "Programming in Python/C++"
        ]
      },
      {
        role: "Cybersecurity Analyst",
        description: "Protect systems and networks from cyber threats",
        requirements: [
          "Cybersecurity or IT-related degree",
          "Knowledge of security tools & protocols",
          "Risk assessment & penetration testing"
        ]
      },
      {
        role: "UX/UI Designer",
        description: "Design user-focused interfaces and experiences",
        requirements: [
          "HCI or design background",
          "Prototyping & user research",
          "Strong communication skills"
        ]
      }
    ]
  },

  // 2) HEALTHCARE & MEDICINE
  {
    id: "healthcare",
    title: "Healthcare & Medicine",
    traits: ["helping", "scientific", "detail-oriented", "collaborative"],
    description:
      "Healthcare professionals improve and save lives through direct patient care, medical research, and leadership in health organizations.",
    suggestedMajors: [
      "Pre-Medicine",
      "Nursing",
      "Biomedical Engineering",
      "Public Health",
      "Healthcare Administration",
      "Biology",
      "Physician Assistant Studies (pre-PA)"
    ],
    topColleges: [
      {
        name: "Johns Hopkins University",
        location: "Baltimore, MD",
        specialization: "Medicine and biomedical research"
      },
      {
        name: "Harvard Medical School",
        location: "Boston, MA",
        specialization: "Medical education and cutting-edge research"
      },
      {
        name: "Stanford Medicine",
        location: "Stanford, CA",
        specialization: "Medical innovation and translational research"
      },
      {
        name: "Duke University",
        location: "Durham, NC",
        specialization: "Strong Pre-Med and Nursing programs"
      }
    ],
    careerPaths: [
      {
        role: "Physician",
        description: "Diagnose and treat patients",
        requirements: [
          "Medical degree (MD or DO)",
          "Residency",
          "Board certification"
        ]
      },
      {
        role: "Biomedical Researcher",
        description: "Conduct research on medical and biological issues",
        requirements: [
          "Ph.D. or MD/Ph.D.",
          "Lab research experience",
          "Scientific expertise"
        ]
      },
      {
        role: "Healthcare Administrator",
        description: "Manage healthcare systems and facilities",
        requirements: [
          "Degree in Healthcare Administration or MBA",
          "Management & leadership skills",
          "Healthcare policy knowledge"
        ]
      },
      {
        role: "Nurse Practitioner",
        description:
          "Provide advanced nursing care, often as a primary care provider",
        requirements: ["BSN", "MSN or DNP", "Clinical experience"]
      },
      {
        role: "Physician Assistant",
        description: "Diagnose illness and assist in patient care",
        requirements: [
          "Undergrad in pre-PA or related field",
          "Master’s in Physician Assistant Studies",
          "Certification & licensure"
        ]
      }
    ]
  },

  // 3) ENGINEERING
  {
    id: "engineering",
    title: "Engineering",
    traits: ["technical", "analytical", "hands-on", "problem-solving", "innovative", "spatial"],
    description:
      "Engineers design, build, and optimize solutions across mechanical, electrical, civil, aerospace, and other specialized domains.",
    suggestedMajors: [
      "Mechanical Engineering",
      "Electrical Engineering",
      "Civil Engineering",
      "Chemical Engineering",
      "Aerospace Engineering",
      "Industrial Engineering",
      "Biomedical Engineering"
    ],
    topColleges: [
      {
        name: "Massachusetts Institute of Technology",
        location: "Cambridge, MA",
        specialization: "Comprehensive engineering programs"
      },
      {
        name: "Stanford University",
        location: "Stanford, CA",
        specialization: "Mechanical, Electrical, and Biomedical Engineering"
      },
      {
        name: "University of Michigan",
        location: "Ann Arbor, MI",
        specialization: "Automotive, Aerospace, and broader Engineering"
      },
      {
        name: "Georgia Institute of Technology",
        location: "Atlanta, GA",
        specialization: "Industrial, Aerospace, and Computer Engineering"
      }
    ],
    careerPaths: [
      {
        role: "Mechanical Engineer",
        description: "Design and develop mechanical systems and devices",
        requirements: ["BS in ME", "CAD & simulation skills", "Analytical mindset"]
      },
      {
        role: "Electrical Engineer",
        description: "Develop electrical systems, circuits, and electronics",
        requirements: [
          "BS in EE",
          "Circuit design & testing",
          "Programming (C/C++/MATLAB)"
        ]
      },
      {
        role: "Aerospace Engineer",
        description: "Design aircraft, spacecraft, and propulsion systems",
        requirements: [
          "BS in Aerospace Engineering",
          "Fluid dynamics & aerodynamics",
          "Materials science"
        ]
      },
      {
        role: "Robotics Engineer",
        description:
          "Integrate mechanical, electrical, and software components into automated systems",
        requirements: [
          "Engineering degree (ME, EE, or related)",
          "Programming",
          "Control systems"
        ]
      },
      {
        role: "Chemical Engineer",
        description: "Develop chemical processes for materials and products",
        requirements: [
          "BS in Chemical Engineering",
          "Process design",
          "Chemistry knowledge"
        ]
      }
    ]
  },

  // 4) BUSINESS & MANAGEMENT
  {
    id: "business",
    title: "Business & Management",
    traits: ["leadership", "analytical", "collaborative", "strategic"],
    description:
      "Business professionals drive growth, create strategies, manage finances, and lead teams in diverse organizational environments.",
    suggestedMajors: [
      "Business Administration",
      "Finance",
      "Marketing",
      "International Business",
      "Entrepreneurship",
      "Management",
      "Accounting"
    ],
    topColleges: [
      {
        name: "University of Pennsylvania (Wharton)",
        location: "Philadelphia, PA",
        specialization: "Finance, Management, and Entrepreneurship"
      },
      {
        name: "New York University (Stern)",
        location: "New York, NY",
        specialization: "Finance, Marketing, and FinTech"
      },
      {
        name: "University of Michigan (Ross)",
        location: "Ann Arbor, MI",
        specialization: "General Business Administration and Strategy"
      },
      {
        name: "UC Berkeley (Haas)",
        location: "Berkeley, CA",
        specialization: "Entrepreneurship, Innovation, and Social Impact"
      }
    ],
    careerPaths: [
      {
        role: "Business Analyst",
        description:
          "Evaluate business processes, identify problems, and suggest improvements",
        requirements: [
          "Business or related degree",
          "Analytical skills",
          "Excellent communication"
        ]
      },
      {
        role: "Marketing Manager",
        description:
          "Develop marketing strategies, oversee campaigns, and analyze consumer data",
        requirements: [
          "Marketing or Business degree",
          "Digital marketing tools",
          "Analytical & creative thinking"
        ]
      },
      {
        role: "Financial Analyst",
        description:
          "Assess financial data to guide investment decisions or corporate strategy",
        requirements: [
          "Finance or Accounting degree",
          "Financial modeling",
          "Excel & analytics"
        ]
      },
      {
        role: "Entrepreneur",
        description:
          "Identify opportunities, build businesses, and manage ventures",
        requirements: [
          "Business acumen",
          "Leadership & risk management",
          "Fundraising & networking"
        ]
      },
      {
        role: "Human Resources Manager",
        description:
          "Oversee recruitment, training, and organizational culture development",
        requirements: [
          "HR or Business degree",
          "People management skills",
          "Knowledge of labor laws"
        ]
      }
    ]
  },

  // 5) CREATIVE ARTS & DESIGN
  {
    id: "creative",
    title: "Creative Arts & Design",
    traits: ["creative", "artistic", "innovative", "detail-oriented"],
    description:
      "Creative professionals bring ideas to life through visual arts, user-centered design, digital media, and innovative storytelling.",
    suggestedMajors: [
      "Graphic Design",
      "Digital Media",
      "User Experience Design",
      "Industrial Design",
      "Fashion Design",
      "Animation",
      "Illustration"
    ],
    topColleges: [
      {
        name: "Rhode Island School of Design",
        location: "Providence, RI",
        specialization: "Fine Arts, Graphic Design, and Industrial Design"
      },
      {
        name: "Parsons School of Design",
        location: "New York, NY",
        specialization: "Fashion, Communication Design, and Strategic Design"
      },
      {
        name: "Savannah College of Art and Design",
        location: "Savannah, GA",
        specialization: "Digital Media, Animation, UX, and Interactive Design"
      },
      {
        name: "ArtCenter College of Design",
        location: "Pasadena, CA",
        specialization: "Industrial, Transportation, and Product Design"
      }
    ],
    careerPaths: [
      {
        role: "UX Designer",
        description: "Design user-friendly digital experiences and interfaces",
        requirements: ["Design or HCI degree", "User research", "Prototyping tools"]
      },
      {
        role: "Graphic Designer",
        description: "Create visual content for branding, marketing, and media",
        requirements: [
          "Design degree",
          "Adobe Creative Suite proficiency",
          "Typography & layout"
        ]
      },
      {
        role: "Industrial Designer",
        description:
          "Develop consumer products, interfaces, and physical prototypes",
        requirements: [
          "Industrial Design degree",
          "3D modeling (CAD)",
          "Prototyping & ergonomics"
        ]
      },
      {
        role: "Art Director",
        description:
          "Lead creative teams to develop cohesive visual concepts for campaigns",
        requirements: [
          "Strong design background",
          "Leadership & project management",
          "Strategic thinking"
        ]
      },
      {
        role: "Animator / Motion Designer",
        description:
          "Create animated visuals for film, TV, games, and marketing content",
        requirements: [
          "Animation or Digital Media degree",
          "3D/2D animation software",
          "Storyboarding & storytelling"
        ]
      }
    ]
  },

  // 6) SCIENCES & RESEARCH
  {
    id: "sciences",
    title: "Sciences & Research",
    traits: ["curious", "analytical", "detail-oriented", "exploratory"],
    description:
      "Scientists investigate the natural world, from subatomic particles to ecosystems, pushing boundaries of knowledge and innovation.",
    suggestedMajors: [
      "Biology",
      "Chemistry",
      "Physics",
      "Environmental Science",
      "Neuroscience",
      "Biochemistry",
      "Mathematics / Statistics"
    ],
    topColleges: [
      {
        name: "Harvard University",
        location: "Cambridge, MA",
        specialization: "Biology, Chemistry, Interdisciplinary Sciences"
      },
      {
        name: "California Institute of Technology (Caltech)",
        location: "Pasadena, CA",
        specialization: "Physics, Astronomy, Engineering-based research"
      },
      {
        name: "University of Chicago",
        location: "Chicago, IL",
        specialization: "Physical Sciences and Mathematics"
      },
      {
        name: "Stanford University",
        location: "Stanford, CA",
        specialization: "Biological Sciences, Interdisciplinary Research"
      }
    ],
    careerPaths: [
      {
        role: "Research Scientist",
        description:
          "Design and conduct experiments in academic, government, or private labs",
        requirements: ["BS in relevant field", "Lab skills", "Often MS or PhD"]
      },
      {
        role: "Environmental Scientist",
        description:
          "Analyze environmental data and develop conservation or sustainability strategies",
        requirements: [
          "Environmental Science degree",
          "Field research experience",
          "Regulatory knowledge"
        ]
      },
      {
        role: "Biotechnologist",
        description:
          "Use biological processes for product development, including pharmaceuticals",
        requirements: [
          "Biology/Biotech degree",
          "Molecular biology techniques",
          "Lab management"
        ]
      },
      {
        role: "Physicist",
        description: "Research fundamental laws of nature and advanced technologies",
        requirements: [
          "BS in Physics",
          "Strong math skills",
          "PhD for advanced research"
        ]
      },
      {
        role: "Data Analyst (Scientific)",
        description:
          "Use statistical and computational tools to interpret scientific data",
        requirements: [
          "Math/Stats or Science degree",
          "Programming (Python/R)",
          "Strong analytical skills"
        ]
      }
    ]
  },

  // 7) MEDIA & COMMUNICATION
  {
    id: "media-communication",
    title: "Media & Communication",
    traits: ["creative", "articulate", "social", "storytelling"],
    description:
      "Media and communication professionals inform, persuade, and entertain through journalism, PR, broadcasting, and content creation.",
    suggestedMajors: [
      "Journalism",
      "Communications",
      "Public Relations",
      "Broadcasting",
      "Media Studies",
      "Digital Media"
    ],
    topColleges: [
      {
        name: "Northwestern University (Medill)",
        location: "Evanston, IL",
        specialization: "Journalism & Integrated Marketing Communications"
      },
      {
        name: "University of Southern California (Annenberg)",
        location: "Los Angeles, CA",
        specialization: "Communication, Journalism, Media Studies"
      },
      {
        name: "Syracuse University (Newhouse)",
        location: "Syracuse, NY",
        specialization: "Broadcast Journalism, Public Relations"
      },
      {
        name: "University of Missouri (Mizzou)",
        location: "Columbia, MO",
        specialization: "Journalism (news reporting & media production)"
      }
    ],
    careerPaths: [
      {
        role: "Journalist",
        description: "Report news and investigate stories for print, TV, or online",
        requirements: [
          "Journalism or Communications degree",
          "Writing & editing skills",
          "Ethical & investigative skills"
        ]
      },
      {
        role: "Public Relations Specialist",
        description:
          "Shape and maintain a brand’s public image through media coverage and campaigns",
        requirements: [
          "PR or Communications degree",
          "Strong writing & interpersonal skills",
          "Crisis management knowledge"
        ]
      },
      {
        role: "Digital Content Creator",
        description:
          "Produce engaging content for social media, blogs, podcasts, or video platforms",
        requirements: [
          "Media/Communications or relevant experience",
          "Editing/production skills",
          "SEO & analytics knowledge"
        ]
      },
      {
        role: "Broadcast Producer",
        description:
          "Plan and coordinate news or entertainment programming for television/radio",
        requirements: [
          "Broadcasting or Media degree",
          "Scriptwriting & production",
          "Team leadership"
        ]
      }
    ]
  },

  // 8) PUBLIC POLICY & LAW
  {
    id: "policy-law",
    title: "Public Policy & Law",
    traits: ["analytical", "persuasive", "ethical", "service-oriented"],
    description:
      "Professionals in law and public policy shape legislation, advocate for justice, and guide organizations through complex regulations.",
    suggestedMajors: [
      "Political Science",
      "Public Policy",
      "International Relations",
      "Legal Studies (Pre-Law)",
      "Criminal Justice",
      "Economics"
    ],
    topColleges: [
      {
        name: "Georgetown University",
        location: "Washington, DC",
        specialization: "Political Science, Foreign Service, Policy"
      },
      {
        name: "Harvard University (Kennedy School at grad level)",
        location: "Cambridge, MA",
        specialization: "Government, Policy, and Law pathways"
      },
      {
        name: "Columbia University",
        location: "New York, NY",
        specialization: "Political Science, Law (graduate), International Affairs"
      },
      {
        name: "University of California, Berkeley",
        location: "Berkeley, CA",
        specialization: "Political Science, Public Policy"
      }
    ],
    careerPaths: [
      {
        role: "Lawyer / Attorney",
        description:
          "Represent clients, interpret laws, and guide legal procedures",
        requirements: [
          "Bachelor’s degree (any major) + JD",
          "Bar exam",
          "Legal research & advocacy"
        ]
      },
      {
        role: "Policy Analyst",
        description:
          "Research and evaluate governmental or organizational policies and their impact",
        requirements: [
          "Public Policy or Political Science degree",
          "Strong analytical & writing skills",
          "Research methodology"
        ]
      },
      {
        role: "Diplomat / Foreign Service Officer",
        description:
          "Represent national interests abroad, negotiate treaties, and manage international relations",
        requirements: [
          "Degree in IR, Political Science, or related",
          "Foreign language skills",
          "Pass Foreign Service exam"
        ]
      },
      {
        role: "Compliance Officer",
        description:
          "Ensure organizations follow laws, regulations, and industry standards",
        requirements: [
          "Legal/Policy background",
          "Risk management",
          "Strong communication skills"
        ]
      }
    ]
  }
];
